/**
 * Idle Timeout Warning + Auto-logout
 *
 * Shows a warning modal when the user has been idle for (timeout - 2 min),
 * then auto-signs out when the full timeout expires.
 *
 * Timeout matrix (workspace × "Keep me logged in" preference):
 *   non-PHI: 30 min default → 30 days when rememberMe is active
 *   PHI:     15 min default → 1 hour when rememberMe is active
 *
 * The 30-day duration is honored by localStorage `expiresAt` (see
 * `utils/rememberMe`). Within-session setTimeout is internally clamped
 * to the JS engine's 2^31-1 ms cap (~24.8 days) by `useIdleTimeout`.
 *
 * The auto-logout is suppressed while a voice recording is in flight (see
 * `stores/recordingActivityStore`) so a long recording isn't signed out
 * mid-capture; the idle window restarts fresh when the recording ends.
 *
 * @compliance GDPR — session security
 * @compliance HIPAA 164.312(a)(2)(iii) — automatic logoff
 */

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { publishIdleActivity, useIdleTimeout } from '../../hooks/ui/useIdleTimeout';
import { useAuth } from '../../contexts/AuthContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { getRememberMe } from '../../utils/rememberMe';
import { useIsRecordingActive } from '../../stores/recordingActivityStore';

const DEFAULT_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const HIPAA_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes (HIPAA requirement)
const REMEMBER_ME_TIMEOUT_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const REMEMBER_ME_HIPAA_TIMEOUT_MS = 60 * 60 * 1000; // 1 hour PHI cap
const WARNING_BEFORE_MS = 2 * 60 * 1000; // Warn 2 minutes before
const RECORDING_PRESENCE_INTERVAL_MS = 60 * 1000; // Recording tab tells the others every minute
// Absolute ceiling on how long a recording may hold off the auto-logout. A
// defence-in-depth backstop so the HIPAA 164.312(a)(2)(iii) control degrades
// safe: even if a recorder never calls endRecording (an exception path, a
// future recorder with no auto-stop, background-tab timer throttling delaying a
// tick-counted auto-stop past real time), the idle timer re-arms after this.
// Set above the 60-minute recording cap so it never fires during a legitimate
// recording — only when suppression is genuinely stuck.
const MAX_SUPPRESSION_MS = 65 * 60 * 1000; // 65 minutes

export function IdleTimeoutWarning() {
  const { signOut, user } = useAuth();
  const { workspace } = useWorkspace();
  const { isOpen, onOpen, onClose } = useDisclosure();

  // A voice recording / live session (mobile capture, "Voice to Text" on a
  // note, or the Live Agent) is active user presence, so we hold off the idle
  // auto-logout while one is in flight — otherwise a >15/30-minute session
  // would be signed out mid-capture and the work lost. The timer starts fresh
  // once it ends.
  const isRecordingActive = useIsRecordingActive();

  // Absolute ceiling: once suppression has run continuously for
  // MAX_SUPPRESSION_MS, stop suppressing even if a recorder still claims to be
  // active, so the auto-logoff can't be held open indefinitely. Reset whenever
  // recording actually ends.
  const [suppressionCapped, setSuppressionCapped] = useState(false);
  useEffect(() => {
    if (!isRecordingActive) {
      setSuppressionCapped(false);
      return;
    }
    const id = setTimeout(() => setSuppressionCapped(true), MAX_SUPPRESSION_MS);
    return () => clearTimeout(id);
  }, [isRecordingActive]);

  // Suppress only while actively recording AND under the ceiling.
  const suppressed = isRecordingActive && !suppressionCapped;

  // PHI workspaces use a shorter timeout per HIPAA 164.312(a)(2)(iii)
  const isPhiWorkspace = useMemo(() => {
    const settings = (workspace as Record<string, unknown> | null)?.settings as
      | Record<string, unknown>
      | undefined;
    return settings?.phi === true;
  }, [workspace]);

  // Recomputed when `user` changes — covers login, logout, and identity
  // enrichment. A component remount (e.g. on workspace switch) also picks
  // up any fresh localStorage state, which is the intended behaviour.
  const rememberMeActive = useMemo(() => {
    const userId = user?.internalId ?? user?.uid;
    if (!userId) return false;
    return getRememberMe(userId) !== null;
  }, [user]);

  const sessionTimeoutMs = useMemo(() => {
    if (isPhiWorkspace) {
      return rememberMeActive ? REMEMBER_ME_HIPAA_TIMEOUT_MS : HIPAA_TIMEOUT_MS;
    }
    return rememberMeActive ? REMEMBER_ME_TIMEOUT_MS : DEFAULT_TIMEOUT_MS;
  }, [isPhiWorkspace, rememberMeActive]);

  const handleTimeout = useCallback(async () => {
    onClose();
    await signOut();
  }, [onClose, signOut]);

  const { resetTimer } = useIdleTimeout({
    timeoutMs: sessionTimeoutMs,
    warningMs: WARNING_BEFORE_MS,
    onWarning: onOpen,
    onTimeout: handleTimeout,
    enabled: !!user && !suppressed,
    onActivityElsewhere: onClose,
  });

  // A recording is presence for EVERY tab, not just this one: a sign-out in
  // any tab signs out all of them, so an idle background tab must not end a
  // capture running here. Publish activity while suppressed, well inside the
  // shortest (15-minute) window. Stops at the suppression ceiling like the
  // rest of the hold-off.
  useEffect(() => {
    if (!user || !suppressed) return;
    publishIdleActivity();
    const id = setInterval(() => publishIdleActivity(), RECORDING_PRESENCE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [user, suppressed]);

  // If a recording starts while the warning is showing, the idle timer is
  // cleared (enabled → false) so the pending sign-out is cancelled — dismiss
  // the now-stale "signing out in 2 minutes" modal too. A fresh window (and a
  // fresh warning, if still idle) starts when the recording ends. Gated on
  // `suppressed`, not `isRecordingActive`, so a warning that fires AFTER the
  // suppression ceiling is reached isn't force-closed.
  useEffect(() => {
    if (suppressed && isOpen) onClose();
  }, [suppressed, isOpen, onClose]);

  const handleStayLoggedIn = useCallback(() => {
    onClose();
    resetTimer();
  }, [onClose, resetTimer]);

  return (
    <Modal isOpen={isOpen} onClose={handleStayLoggedIn} isCentered size="sm">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Session expiring</ModalHeader>
        <ModalBody>
          <Text color="text.secondary">
            You have been inactive for a while. For security, you will be signed out in 2 minutes
            unless you choose to stay logged in.
          </Text>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="outline" onClick={handleTimeout} size="sm">
            Sign out now
          </Button>
          <Button onClick={handleStayLoggedIn} size="sm">
            Stay logged in
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
